import { useCallback, useLayoutEffect, useRef } from 'react';

type ConversationScopeState = {
  conversationId: string | null;
  scopeVersion: number;
  isMounted: boolean;
};

export type ConversationScope = Pick<
  ConversationScopeState,
  'conversationId' | 'scopeVersion'
>;

/**
 * Tracks whether asynchronous work still belongs to the mounted conversation scope.
 * A null-to-created transition remains current only for the created conversation id.
 * @param activeConversationId - Currently selected conversation identifier
 * @param scopeVersion - Version advanced whenever the chat is reset
 * @returns Functions for capturing and validating conversation scope tokens
 */
export const useConversationScope = (
  activeConversationId: string | null,
  scopeVersion: number
) => {
  const scopeRef = useRef<ConversationScopeState>({
    conversationId: activeConversationId,
    scopeVersion,
    isMounted: true,
  });

  useLayoutEffect(() => {
    scopeRef.current.conversationId = activeConversationId;
    scopeRef.current.scopeVersion = scopeVersion;
  }, [activeConversationId, scopeVersion]);

  useLayoutEffect(() => {
    scopeRef.current.isMounted = true;
    return () => {
      scopeRef.current.isMounted = false;
    };
  }, []);

  const captureScope = useCallback((): ConversationScope => {
    return {
      conversationId: scopeRef.current.conversationId,
      scopeVersion: scopeRef.current.scopeVersion,
    };
  }, []);
  const isScopeCurrent = useCallback(
    (scope: ConversationScope, createdConversationId?: string): boolean => {
      if (
        !scopeRef.current.isMounted ||
        scopeRef.current.scopeVersion !== scope.scopeVersion
      ) {
        return false;
      }
      return (
        scopeRef.current.conversationId === scope.conversationId ||
        (scope.conversationId === null &&
          scopeRef.current.conversationId === createdConversationId)
      );
    },
    []
  );

  return { captureScope, isScopeCurrent };
};
